import { useState } from 'react';

import { useTranslations } from '@/i18n/use-locale';
import { adminInitials, ALL_ADMIN_ROLES, type AdminRole, type AdminTeamMember } from '@/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { useAdminTeamQuery } from '@/lib/queries';
import { useInviteAdminMutation } from '@/lib/mutations';
import { StatusBadge } from '@/components/ui/status-badge';
import { Card } from '@/components/ui/card';

/**
 * فريق الأدمن (design 992-1008) — the reviewers who work the verification and
 * document queues (C5). The list, the invite, and the one-time password are all
 * real: `/admin/admins` is the same CRUD `bootstrap-admin` seeds the first
 * account through, only authenticated and audited. The invited account changes
 * its password and enrols TOTP on first sign-in, so the password shown here is
 * shown exactly once.
 */
export function AdminTeamPanel() {
  const t = useTranslations('admin.settings');
  const [inviting, setInviting] = useState(false);

  const team = useAdminTeamQuery();

  const members = team.data ?? [];

  return (
    <Card className="p-5">
      <h2 className="text-[15px] font-bold text-navy">{t('team.heading')}</h2>
      <p className="mb-4 text-[12.5px] text-conc-500">{t('team.hint')}</p>

      <ul className="divide-y divide-conc-50">
        {members.map((member) => (
          <MemberRow key={member.id} member={member} />
        ))}
        {team.isLoading ? (
          <li className="py-3 text-[13px] text-conc-400">{t('team.loading')}</li>
        ) : null}
        {!team.isLoading && members.length === 0 ? (
          <li className="py-3 text-[13px] text-conc-400">{t('team.empty')}</li>
        ) : null}
      </ul>

      {inviting ? (
        <InviteForm onClose={() => setInviting(false)} />
      ) : (
        <Button variant="outline" onClick={() => setInviting(true)} className="mt-3 w-full">
          {t('team.invite')}
        </Button>
      )}
    </Card>
  );
}

function MemberRow({ member }: { member: AdminTeamMember }) {
  const t = useTranslations('admin.settings');
  const active = member.status === 'active';
  return (
    <li className="flex items-center gap-3 py-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-navy text-[11px] font-bold text-white">
        {adminInitials(member.fullName)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13.5px] font-semibold text-navy">{member.fullName}</p>
        <p className="truncate text-[12px] text-conc-500">{t(`team.role.${member.role}`)}</p>
      </div>
      <StatusBadge tone={active ? 'approved' : 'neutral'}>
        {active ? t('team.status.active') : t('team.status.disabled')}
      </StatusBadge>
    </li>
  );
}

function InviteForm({ onClose }: { onClose: () => void }) {
  const t = useTranslations('admin.settings');
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<AdminRole>('document_auditor');
  const [password, setPassword] = useState<string | null>(null);

  const invite = useInviteAdminMutation({
    okToast: t('team.toast.invited'),
    failToast: t('team.toast.failed'),
    onSuccess: (invited) => setPassword(invited.temporaryPassword),
  });

  // Shown once. The account exists; this is the only time the password is legible.
  if (password !== null) {
    return (
      <div className="mt-3 space-y-2 rounded-xl border border-pitch-100 bg-pitch-50 p-4">
        <p className="text-[12.5px] font-semibold text-pitch-800">{t('team.passwordShownOnce')}</p>
        <div className="flex items-center gap-2">
          <code className="num flex-1 truncate rounded-lg bg-white px-3 py-2 text-[13px] font-bold text-navy">
            {password}
          </code>
          <Button
            variant="outline"
            onClick={() => {
              void navigator.clipboard?.writeText(password);
              toast({ title: t('team.toast.copied') });
            }}
          >
            {t('team.copy')}
          </Button>
        </div>
        <Button className="w-full" onClick={onClose}>
          {t('team.done')}
        </Button>
      </div>
    );
  }

  const valid = email.trim().length > 3 && fullName.trim().length > 1;

  return (
    <form
      className="mt-3 space-y-3 rounded-xl border border-conc-100 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && !invite.isPending)
          invite.mutate({ email: email.trim(), fullName: fullName.trim(), role });
      }}
    >
      <label className="block space-y-1 text-[12.5px]">
        <span className="font-semibold text-conc-500">{t('team.field.fullName')}</span>
        <Input value={fullName} onChange={(event) => setFullName(event.target.value)} required />
      </label>
      <label className="block space-y-1 text-[12.5px]">
        <span className="font-semibold text-conc-500">{t('team.field.email')}</span>
        <Input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
      </label>
      <label className="block space-y-1 text-[12.5px]">
        <span className="font-semibold text-conc-500">{t('team.field.role')}</span>
        <select
          value={role}
          onChange={(event) => setRole(event.target.value as AdminRole)}
          className="w-full rounded-lg border border-conc-100 bg-sideline px-2.5 py-2 text-[13px] text-navy outline-none focus:border-pitch focus:bg-white"
        >
          {ALL_ADMIN_ROLES.map((option) => (
            <option key={option} value={option}>
              {t(`team.role.${option}`)}
            </option>
          ))}
        </select>
      </label>
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" type="button" onClick={onClose} disabled={invite.isPending}>
          {t('cancel')}
        </Button>
        <Button type="submit" disabled={!valid || invite.isPending}>
          {t('team.sendInvite')}
        </Button>
      </div>
    </form>
  );
}
